<template>
  <svg
    ref="schematicSvg"
    viewBox="0 -120 900 440"
    xmlns="http://www.w3.org/2000/svg"
    class="w-100"
    :class="{ 'animations-paused': animationsPaused }"
    style="display: block; touch-action: none"
    aria-label="Pump test stand schematic"
    aria-describedby="valve-instructions-desc"
    role="img"
  >
    <!-- Screen reader description for valve operation -->
    <desc id="valve-instructions-desc">
      Interactive pump test stand diagram. The valve handwheel can be turned
      clockwise to open the valve and increase flow rate, or counterclockwise to
      close the valve and decrease flow rate. Use arrow keys or drag the
      handwheel to adjust the valve position.
    </desc>
    <!-- Pipe runs -->
    <!-- Suction horizontal: tank → pump bottom -->
    <line
      x1="60"
      y1="220"
      x2="341"
      y2="220"
      stroke="#94a3b8"
      stroke-width="14"
      stroke-linecap="round"
    />
    <!-- Suction vertical: pump bottom port → suction line -->
    <line
      x1="341"
      y1="204"
      x2="341"
      y2="220"
      stroke="#94a3b8"
      stroke-width="14"
      stroke-linecap="round"
    />
    <!-- Discharge vertical: pump top port → discharge line -->
    <line
      x1="341"
      y1="100"
      x2="341"
      y2="116"
      stroke="#94a3b8"
      stroke-width="14"
      stroke-linecap="round"
    />
    <!-- Discharge horizontal: pump → I-2 → valve -->
    <line
      x1="341"
      y1="100"
      x2="488"
      y2="100"
      stroke="#94a3b8"
      stroke-width="14"
      stroke-linecap="round"
    />
    <line
      x1="536"
      y1="100"
      x2="595"
      y2="100"
      stroke="#94a3b8"
      stroke-width="14"
      stroke-linecap="round"
    />
    <line
      x1="705"
      y1="100"
      x2="780"
      y2="100"
      stroke="#94a3b8"
      stroke-width="14"
      stroke-linecap="round"
    />
    <!-- Return: FT → around → back to tank -->
    <polyline
      points="840,100 875,100 875,295 10,295 10,170 67,170 67,180"
      fill="none"
      stroke="#94a3b8"
      stroke-width="14"
      stroke-linejoin="round"
      stroke-linecap="round"
    />

    <!-- Fluid flow overlays — blue dashes animated by GSAP, speed ∝ flow rate -->
    <polyline
      ref="flowSuction"
      points="60,220 341,220 341,204"
      fill="none"
      stroke="rgba(59,130,246,0.7)"
      stroke-width="7"
      stroke-linejoin="round"
      stroke-linecap="round"
      stroke-dasharray="16 12"
      stroke-dashoffset="0"
    />
    <polyline
      ref="flowDischarge1"
      points="341,116 341,100 488,100"
      fill="none"
      stroke="rgba(59,130,246,0.7)"
      stroke-width="7"
      stroke-linejoin="round"
      stroke-linecap="round"
      stroke-dasharray="16 12"
      stroke-dashoffset="0"
    />
    <line
      ref="flowDischarge2"
      x1="536"
      y1="100"
      x2="595"
      y2="100"
      stroke="rgba(59,130,246,0.7)"
      stroke-width="7"
      stroke-linecap="round"
      stroke-dasharray="16 12"
      stroke-dashoffset="0"
    />
    <line
      ref="flowDischarge3"
      x1="705"
      y1="100"
      x2="840"
      y2="100"
      stroke="rgba(59,130,246,0.7)"
      stroke-width="7"
      stroke-linecap="round"
      stroke-dasharray="16 12"
      stroke-dashoffset="0"
    />
    <polyline
      ref="flowReturn"
      points="840,100 875,100 875,295 10,295 10,170 67,170 67,180"
      fill="none"
      stroke="rgba(59,130,246,0.7)"
      stroke-width="7"
      stroke-linejoin="round"
      stroke-linecap="round"
      stroke-dasharray="16 12"
      stroke-dashoffset="0"
    />

    <!-- Tank -->
    <rect
      x="25"
      y="180"
      width="85"
      height="80"
      rx="6"
      fill="#dbeafe"
      stroke="#3b82f6"
      stroke-width="2.5"
    />
    <path
      d="M32 245 q11-9 22 0 q11 9 22 0 q11-9 22 0"
      fill="none"
      stroke="#3b82f6"
      stroke-width="2"
    />
    <text
      x="67"
      y="207"
      text-anchor="middle"
      font-size="13"
      fill="#1e40af"
      font-weight="600"
      font-family="sans-serif"
    >
      Tank
    </text>

    <!-- Suction Gauge I-1 -->
    <circle
      cx="215"
      cy="220"
      r="26"
      fill="white"
      stroke="#6b7280"
      stroke-width="2.5"
    />
    <path
      d="M194 233 A26 26 0 0 1 236 233"
      fill="none"
      stroke="#e5e7eb"
      stroke-width="2"
    />
    <line
      x1="215"
      y1="220"
      x2="215"
      y2="198"
      stroke="#dc2626"
      stroke-width="2.5"
      stroke-linecap="round"
    />
    <circle cx="215" cy="220" r="4" fill="#6b7280" />
    <text
      x="215"
      y="257"
      text-anchor="middle"
      font-size="12"
      fill="#374151"
      font-family="sans-serif"
    >
      I-1
    </text>

    <!-- Pump -->
    <g :class="{ 'pump-shake': pumpOverheat }">
      <circle
        cx="341"
        cy="160"
        r="44"
        fill="#eff6ff"
        :stroke="pumpOverheat ? '#dc2626' : '#3b82f6'"
        stroke-width="3"
      />
      <g stroke="#3b82f6" stroke-width="2.5" fill="none">
        <line x1="341" y1="130" x2="341" y2="118" />
        <line x1="362" y1="139" x2="371" y2="130" />
        <line x1="385" y1="160" x2="397" y2="160" />
        <line x1="362" y1="181" x2="371" y2="190" />
        <line x1="341" y1="190" x2="341" y2="202" />
        <line x1="320" y1="181" x2="311" y2="190" />
        <line x1="297" y1="160" x2="285" y2="160" />
        <line x1="320" y1="139" x2="311" y2="130" />
      </g>
      <!-- Spinning impeller — GSAP rotates continuously, speed ∝ flow -->
      <g ref="pumpImpeller">
        <circle
          cx="341"
          cy="160"
          r="14"
          fill="#dbeafe"
          stroke="#3b82f6"
          stroke-width="2"
        />
        <line
          x1="341"
          y1="160"
          x2="341"
          y2="147"
          stroke="#3b82f6"
          stroke-width="2.5"
          stroke-linecap="round"
        />
        <line
          x1="341"
          y1="160"
          x2="353"
          y2="153"
          stroke="#3b82f6"
          stroke-width="2.5"
          stroke-linecap="round"
        />
        <line
          x1="341"
          y1="160"
          x2="353"
          y2="167"
          stroke="#3b82f6"
          stroke-width="2.5"
          stroke-linecap="round"
        />
        <line
          x1="341"
          y1="160"
          x2="341"
          y2="173"
          stroke="#3b82f6"
          stroke-width="2.5"
          stroke-linecap="round"
        />
        <line
          x1="341"
          y1="160"
          x2="329"
          y2="167"
          stroke="#3b82f6"
          stroke-width="2.5"
          stroke-linecap="round"
        />
        <line
          x1="341"
          y1="160"
          x2="329"
          y2="153"
          stroke="#3b82f6"
          stroke-width="2.5"
          stroke-linecap="round"
        />
        <circle cx="341" cy="160" r="4" fill="#3b82f6" />
      </g>
    </g>
    <text
      x="405"
      y="163"
      text-anchor="start"
      font-size="13"
      fill="#1e40af"
      font-weight="600"
      font-family="sans-serif"
    >
      Pump
    </text>

    <!-- Pump Power Switch — mounted on pump body -->
    <g
      @click="$emit('toggle-pump')"
      @keydown.enter.prevent="$emit('toggle-pump')"
      @keydown.space.prevent="$emit('toggle-pump')"
      tabindex="0"
      style="cursor: pointer"
      role="switch"
      :aria-checked="pumpOn"
      :aria-label="pumpOn ? 'Turn pump off' : 'Turn pump on'"
    >
      <!-- switch track -->
      <rect
        x="405"
        y="168"
        width="36"
        height="18"
        rx="9"
        :fill="pumpOn ? '#22c55e' : '#d1d5db'"
        stroke="#6b7280"
        stroke-width="1.5"
        style="transition: fill 0.2s"
      />
      <!-- switch knob -->
      <circle
        :cx="pumpOn ? 432 : 414"
        cy="177"
        r="7"
        fill="white"
        stroke="#6b7280"
        stroke-width="1.5"
        style="transition: cx 0.2s"
      />
      <text
        x="423"
        y="199"
        text-anchor="middle"
        font-size="9"
        :fill="pumpOn ? '#16a34a' : '#6b7280'"
        font-weight="600"
        font-family="sans-serif"
      >
        {{ pumpOn ? "ON" : "OFF" }}
      </text>
    </g>

    <!-- Sound wave arcs (visible when pump overheats) -->
    <g v-if="pumpOverheat" class="pump-sound-waves" pointer-events="none">
      <path
        d="M 390 140 A 55 55 0 0 1 390 180"
        fill="none"
        stroke="#dc2626"
        stroke-width="2.5"
        stroke-linecap="round"
        class="sound-wave wave-1"
      />
      <path
        d="M 400 132 A 70 70 0 0 1 400 188"
        fill="none"
        stroke="#dc2626"
        stroke-width="2"
        stroke-linecap="round"
        class="sound-wave wave-2"
      />
      <path
        d="M 410 124 A 85 85 0 0 1 410 196"
        fill="none"
        stroke="#dc2626"
        stroke-width="1.5"
        stroke-linecap="round"
        class="sound-wave wave-3"
      />
    </g>

    <!-- Discharge Gauge I-2 -->
    <circle
      cx="512"
      cy="100"
      r="26"
      fill="white"
      stroke="#6b7280"
      stroke-width="2.5"
    />
    <path
      d="M491 113 A26 26 0 0 1 533 113"
      fill="none"
      stroke="#e5e7eb"
      stroke-width="2"
    />
    <line
      ref="gaugeNeedle"
      x1="512"
      y1="100"
      x2="512"
      y2="78"
      stroke="#dc2626"
      stroke-width="2.5"
      stroke-linecap="round"
    />
    <circle cx="512" cy="100" r="4" fill="#6b7280" />
    <text
      x="512"
      y="137"
      text-anchor="middle"
      font-size="12"
      fill="#374151"
      font-family="sans-serif"
    >
      I-2
    </text>

    <!-- Gate Valve group — GSAP translates vertically -->
    <g ref="valveGroup">
      <!-- pipe flanges -->
      <rect
        x="595"
        y="87"
        width="20"
        height="26"
        rx="3"
        fill="#9ca3af"
        stroke="#6b7280"
        stroke-width="1"
      />
      <rect
        x="685"
        y="87"
        width="20"
        height="26"
        rx="3"
        fill="#9ca3af"
        stroke="#6b7280"
        stroke-width="1"
      />
      <!-- valve body -->
      <rect
        x="613"
        y="72"
        width="74"
        height="56"
        rx="6"
        fill="#d1d5db"
        stroke="#6b7280"
        stroke-width="2"
      />
      <!-- bonnet -->
      <rect
        x="633"
        y="22"
        width="34"
        height="52"
        rx="4"
        fill="#d1d5db"
        stroke="#6b7280"
        stroke-width="2"
      />
      <!-- stem -->
      <line
        x1="650"
        y1="-30"
        x2="650"
        y2="24"
        stroke="#9ca3af"
        stroke-width="6"
        stroke-linecap="round"
      />
      <!-- gate disc — GSAP tweens attr.y -->
      <rect
        ref="gateDisc"
        x="618"
        y="82"
        width="64"
        height="20"
        rx="3"
        fill="#6b7280"
        stroke="#374151"
        stroke-width="1.5"
      />
      <!-- Handwheel outer rim (draggable + keyboard accessible) -->
      <circle
        ref="handwheel"
        cx="650"
        cy="-34"
        r="32"
        fill="#e0e7ff"
        stroke="#6366f1"
        stroke-width="4.5"
        style="cursor: grab"
        tabindex="0"
        role="slider"
        :aria-valuenow="valvePosition"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label="Valve opening"
        :aria-valuetext="valvePosition + '% open'"
        @pointerdown.prevent="$emit('handwheel-pointerdown', $event)"
        @keydown="$emit('handwheel-keydown', $event)"
        @focus="$emit('handwheel-focus')"
        @blur="$emit('handwheel-blur')"
      />
      <!-- Handwheel spokes + lever — GSAP rotates this group -->
      <g ref="handwheelSpokes" style="pointer-events: none">
        <line
          x1="618"
          y1="-34"
          x2="682"
          y2="-34"
          stroke="#6366f1"
          stroke-width="3.5"
        />
        <line
          x1="650"
          y1="-66"
          x2="650"
          y2="-2"
          stroke="#6366f1"
          stroke-width="3.5"
        />
        <line
          x1="627"
          y1="-57"
          x2="673"
          y2="-11"
          stroke="#6366f1"
          stroke-width="2"
        />
        <line
          x1="673"
          y1="-57"
          x2="627"
          y2="-11"
          stroke="#6366f1"
          stroke-width="2"
        />
        <!-- Lever handle — pointer events enabled so it can be grabbed -->
        <line
          x1="682"
          y1="-34"
          x2="724"
          y2="-34"
          stroke="#6366f1"
          stroke-width="7"
          stroke-linecap="round"
          style="pointer-events: all; cursor: grab"
          @pointerdown.prevent="$emit('handwheel-pointerdown', $event)"
        />
        <circle
          cx="724"
          cy="-34"
          r="9"
          fill="#6366f1"
          stroke="#4338ca"
          stroke-width="2"
          style="pointer-events: all; cursor: grab"
          @pointerdown.prevent="$emit('handwheel-pointerdown', $event)"
        />
      </g>
      <!-- hub -->
      <circle
        cx="650"
        cy="-34"
        r="8"
        fill="#6366f1"
        style="pointer-events: none"
      />
    </g>

    <!-- Valve labels outside animated group -->
    <text
      x="650"
      y="148"
      text-anchor="middle"
      font-size="13"
      fill="#374151"
      font-weight="600"
      font-family="sans-serif"
    >
      Valve
    </text>
    <text
      ref="valvePctLabel"
      x="650"
      y="162"
      text-anchor="middle"
      font-size="11"
      fill="#6b7280"
      font-family="sans-serif"
    >
      0%
    </text>
    <text
      x="540"
      y="-40"
      text-anchor="end"
      font-size="12"
      fill="#166534"
      font-weight="600"
      font-family="sans-serif"
    >
      ↻ clockwise to open (higher flow)
    </text>
    <text
      x="540"
      y="-24"
      text-anchor="end"
      font-size="12"
      fill="#7c2d12"
      font-weight="600"
      font-family="sans-serif"
    >
      ↺ counterclockwise to close
    </text>

    <!-- Flow Meter -->
    <rect
      x="780"
      y="76"
      width="60"
      height="48"
      rx="6"
      fill="white"
      stroke="#6b7280"
      stroke-width="2.5"
    />
    <circle
      cx="810"
      cy="100"
      r="18"
      fill="none"
      stroke="#10b981"
      stroke-width="2.5"
    />
    <!-- FT needle — GSAP rotates ∝ flow rate -->
    <line
      ref="ftNeedle"
      x1="810"
      y1="100"
      x2="810"
      y2="84"
      stroke="#10b981"
      stroke-width="2"
      stroke-linecap="round"
    />
    <circle cx="810" cy="100" r="4" fill="#10b981" />
    <text
      x="810"
      y="142"
      text-anchor="middle"
      font-size="13"
      fill="#374151"
      font-family="sans-serif"
    >
      FT
    </text>
  </svg>
</template>
<script>
export default {
  name: "PumpTestStandSchematic",
  props: {
    animationsPaused: { type: Boolean, default: false },
    pumpOn: { type: Boolean, required: true },
    valvePosition: { type: Number, required: true },
    pumpOverheat: { type: Boolean, required: true }
  },
  emits: [
    "toggle-pump",
    "handwheel-pointerdown",
    "handwheel-keydown",
    "handwheel-focus",
    "handwheel-blur"
  ]
};
</script>

<style scoped>
/* Pump shaking animation when overheating */
.pump-shake {
  animation: shake 0.15s infinite alternate;
  transform-box: fill-box;
  transform-origin: center center;
}

@keyframes shake {
  0% {
    transform: translate(-1.5px, 0.5px) rotate(-0.5deg);
  }
  25% {
    transform: translate(1.5px, -0.5px) rotate(0.5deg);
  }
  50% {
    transform: translate(-0.5px, 1px) rotate(-0.3deg);
  }
  75% {
    transform: translate(1px, -1px) rotate(0.3deg);
  }
  100% {
    transform: translate(0.5px, 0.5px) rotate(0.5deg);
  }
}

/* Sound wave arcs pulsing outward */
.sound-wave {
  opacity: 0;
  animation: pulse-wave 1.2s ease-out infinite;
  transform-box: fill-box;
  transform-origin: left center;
}
.wave-1 {
  animation-delay: 0s;
}
.wave-2 {
  animation-delay: 0.4s;
}
.wave-3 {
  animation-delay: 0.8s;
}

.animations-paused .pump-shake,
.animations-paused .sound-wave {
  animation-play-state: paused;
}

@keyframes pulse-wave {
  0% {
    opacity: 0.9;
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateX(8px);
  }
}

/* Keyboard focus ring for SVG handwheel (WCAG 2.4.7) */
circle[role="slider"]:focus {
  outline: 3px solid #4338ca;
  outline-offset: 2px;
}
circle[role="slider"]:focus:not(:focus-visible) {
  outline: none;
}
</style>
